import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allMovies, categories } from "@/lib/movies";
import { contentScope, friendlyLinks, siteFaq, teamMembers } from "@/mocks/site";

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 韩国伦理电影在线观看",
    description:
      "了解韩国伦理电影在线观看的站点定位、内容范围、编辑团队与版权声明。我们专注韩国伦理题材影片的资料整理、分类推荐与观影界面演示。",
    keywords: "关于韩国伦理电影在线观看,韩国伦理电影,伦理电影网站,韩影资料库",
  });

  return (
    <Layout>
      <div className="relative">
        <div className="absolute inset-0 h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20Korean%20film%20archive%20interior%2C%20tall%20wooden%20shelves%20of%20film%20canisters%20lit%20by%20warm%20amber%20lamps%2C%20deep%20charcoal%20shadows%2C%20dust%20in%20the%20air%2C%20quiet%20editorial%20atmosphere%2C%20no%20people%2C%20no%20text&width=1600&height=800&seq=about-hero-2026&orientation=landscape"
            alt="影视资料馆内部氛围"
            title="关于韩国伦理电影在线观看"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/80 to-background-50/40"></div>
        </div>

        <div className="relative px-4 md:px-8 lg:px-12 pt-7 pb-10">
          <div className="max-w-shell mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <p className="mt-5 font-label text-xs tracking-[0.32em] text-primary-500 mb-2">ABOUT US</p>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold max-w-2xl">
              关于韩国伦理电影在线观看
            </h1>
            <p className="mt-3 text-sm md:text-[15px] text-foreground-700 leading-relaxed max-w-2xl">
              我们是一个专注韩国伦理题材电影的资料型站点，用清晰的分类、完整的影片信息与克制的界面，
              帮助成年影迷更快地找到想看的作品。
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              {[
                { value: `${allMovies.length}`, label: "影片条目" },
                { value: `${categories.length - 1}`, label: "内容分类" },
                { value: "2024", label: "最近更新" },
                { value: "100%", label: "资料人工校对" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-md border border-background-200/70 bg-background-50/80 px-4 py-2.5 min-w-[96px]"
                >
                  <p className="font-label text-xl text-primary-500 leading-none">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="max-w-shell mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <section>
              <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-4">站点定位</h2>
              <div className="space-y-4 text-sm leading-[1.9] text-foreground-700">
                <p>
                  韩国伦理电影在线观看面向喜爱韩国伦理电影的成年观众，围绕「找片 — 了解 — 观影」这条路径组织内容。
                  我们把影片资料放在最前面：导演、主演、片长、上映时间、类型标签与剧情简介，都经过统一格式整理，
                  方便快速比较与判断。
                </p>
                <p>
                  站内目前收录 {allMovies.length} 部虚构示例影片，覆盖剧情伦理、爱情伦理与社会伦理三个方向。
                  我们更关心影片如何讲述人与人之间的关系，因此在分类上强调题材与情绪，而不是简单的热度排序。
                </p>
                <p>
                  需要说明的是，站点当前不提供真实影片播放服务。播放区域为前端界面演示，用于展示观影流程、
                  加载状态与操作控件，所有影片条目、人物与剧情均为虚构示例，不对应任何真实作品。
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-4">内容范围</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {contentScope.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200/70 bg-background-100/40 p-4"
                  >
                    <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <h3 className="text-sm font-medium text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="mt-14 md:mt-16">
            <SectionHeading
              eyebrow="OUR TEAM"
              title="编辑团队"
              description="一支规模很小但很较真的团队，负责选片、资料校对与站点体验。以下成员为示例介绍。"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="rounded-lg border border-background-200/70 bg-background-50 p-5"
                >
                  <span className="w-11 h-11 rounded-full bg-primary-500/15 text-primary-400 font-label text-base flex items-center justify-center">
                    {member.initials}
                  </span>
                  <h3 className="mt-4 font-heading text-base text-foreground-950 font-semibold">{member.name}</h3>
                  <p className="text-[11px] text-primary-500 mt-1">{member.role}</p>
                  <p className="mt-2.5 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-14 grid lg:grid-cols-2 gap-8">
            <section>
              <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-4">常见问题</h2>
              <div className="rounded-lg border border-background-200/70 bg-background-100/40 divide-y divide-background-200/60">
                {siteFaq.map((item) => (
                  <details key={item.q} className="group px-5 py-4">
                    <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                      <span className="text-sm text-foreground-900 font-medium">{item.q}</span>
                      <i
                        className="ri-add-line text-foreground-500 group-open:rotate-45 transition-transform"
                        aria-hidden="true"
                      ></i>
                    </summary>
                    <p className="mt-2.5 text-xs leading-relaxed text-foreground-600">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-4">友情链接</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {friendlyLinks.map((link) => (
                  <div
                    key={link.name}
                    className="rounded-lg border border-background-200/70 bg-background-100/40 px-4 py-3.5 hover:border-primary-400/50 transition-colors"
                  >
                    <p className="text-sm text-foreground-900">{link.name}</p>
                    <p className="mt-1 text-[11px] text-foreground-500">{link.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-foreground-500 leading-relaxed">
                以上友情链接为站点资料示例，展示形式仅用于页面结构说明。
              </p>
            </section>
          </div>

          <section className="mt-14 rounded-lg border border-secondary-200/50 bg-secondary-100/40 p-6 md:p-7">
            <h2 className="font-heading text-lg text-foreground-950 font-semibold mb-3">版权与免责声明</h2>
            <div className="space-y-2.5 text-xs md:text-sm leading-relaxed text-foreground-700">
              <p>
                本站所有影片名称、导演、演员、剧情简介、评分与海报均为虚构示例内容，仅用于产品演示，
                如与现实作品或人物重合，纯属巧合。
              </p>
              <p>
                本站不存储、不传播任何视频文件，也不提供真实影片的播放、下载或观看链接。
                播放区域仅为前端界面与交互演示。
              </p>
              <p>
                站内内容仅面向成年观众。请支持正版影视作品，通过合法授权的平台观看相关影片。
              </p>
            </div>
          </section>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center gap-2 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line" aria-hidden="true"></i>
              联系我们
            </Link>
            <Link
              to="/category/all"
              className="h-10 px-5 rounded-md border border-background-300 text-foreground-800 text-sm flex items-center gap-2 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部片库
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}